import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import { MetricCard } from '../components/MetricCard';
import { InsightCard } from '../components/InsightCard';
import { CrossModuleWidgets } from '../components/CrossModuleWidgets';
import { PendingImports } from '../components/PendingImports';
import { SmartUploadZone } from '../components/SmartUploadZone';
import { POSSyncWidget } from '../components/POSSyncWidget';
import { DollarSign, TrendingUp, Users, Receipt, Sparkles } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export const Dashboard = () => {
  const { selectedClient, API } = useClient();
  const [metrics, setMetrics] = useState(null);
  const [priorMetrics, setPriorMetrics] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 112: default to the last completed calendar month rather than
  // a rolling 30-day cross-month slice — the KPIs anchor to a real month.
  const [anchor, setAnchor] = useState('last_completed_month');
  const [period, setPeriod] = useState(30);
  const [anchorLabel, setAnchorLabel] = useState('');
  const [windowMeta, setWindowMeta] = useState(null);

  const fetchDashboardData = async () => {
    if (!selectedClient) return;

    setLoading(true);
    try {
      const url =
        anchor === 'last_completed_month'
          ? `${API}/dashboard/${selectedClient.id}?anchor=last_completed_month&auto_fallback=true`
          : `${API}/dashboard/${selectedClient.id}?anchor=rolling&days=${period}&auto_fallback=true`;
      const response = await axios.get(url);
      setMetrics(response.data.metrics);
      setPriorMetrics(response.data.prior_metrics || null);
      setAnchorLabel(response.data.anchor_label || '');
      setInsights(response.data.insights || []);
      setWindowMeta({
        fallback_applied: !!response.data.fallback_applied,
        fallback_reason: response.data.fallback_reason || '',
        period: response.data.period || null,
        requested_period: response.data.requested_period || null,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [selectedClient, anchor, period]);

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="no-client-selected">
        <p className="text-text-secondary">Please select a client to view dashboard</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Financial Dashboard</h2>
          <p className="text-text-secondary mt-1">
            {selectedClient.name} — {selectedClient.location}
            {anchorLabel && (
              <span className="ml-2 text-primary font-medium" data-testid="dashboard-anchor-label">
                · {anchorLabel}
              </span>
            )}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            key="lcm"
            onClick={() => setAnchor('last_completed_month')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              anchor === 'last_completed_month'
                ? 'bg-primary text-white'
                : 'bg-white border border-border text-text-secondary hover:border-primary'
            }`}
            data-testid="period-last-completed-month"
          >
            Last month
          </button>
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              onClick={() => { setAnchor('rolling'); setPeriod(days); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                anchor === 'rolling' && period === days
                  ? 'bg-primary text-white'
                  : 'bg-white border border-border text-text-secondary hover:border-primary'
              }`}
              data-testid={`period-${days}-days`}
            >
              {days}D
            </button>
          ))}
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-upload">Upload Data</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <POSSyncWidget clientId={selectedClient.id} API={API} />
          {windowMeta?.fallback_applied && windowMeta.period && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
              data-testid="dashboard-fallback-banner"
              role="status"
            >
              <span className="mt-0.5">⚠️</span>
              <div>
                <div className="font-medium">Showing your latest data window</div>
                <div className="text-amber-800">
                  {windowMeta.fallback_reason} Window:{' '}
                  <span className="font-mono">{windowMeta.period.start_date}</span> →{' '}
                  <span className="font-mono">{windowMeta.period.end_date}</span>.
                </div>
              </div>
            </div>
          )}
          <CrossModuleWidgets clientId={selectedClient.id} API={API} />

          {metrics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                  label="Total Revenue"
                  value={`₹${metrics.total_revenue.toLocaleString()}`}
                  icon={DollarSign}
                  data-testid="metric-revenue"
                  subtext={priorMetrics && priorMetrics.total_revenue > 0
                    ? `${((metrics.total_revenue - priorMetrics.total_revenue) / priorMetrics.total_revenue * 100).toFixed(1)}% vs prior month`
                    : undefined}
                />
                <MetricCard
                  label="EBITDA"
                  value={`₹${(metrics.ebitda ?? metrics.pure_profit ?? 0).toLocaleString()}`}
                  icon={TrendingUp}
                  data-testid="metric-ebitda"
                  subtext={priorMetrics && priorMetrics.ebitda !== undefined
                    ? `${(metrics.ebitda - priorMetrics.ebitda).toLocaleString()} vs prior month`
                    : undefined}
                />
                <MetricCard
                  label="Food Cost"
                  value={metrics.food_cost_percentage.toFixed(1)}
                  unit="%"
                  icon={Receipt}
                  data-testid="metric-food-cost"
                  subtext={priorMetrics && priorMetrics.food_cost_percentage !== undefined
                    ? `vs ${priorMetrics.food_cost_percentage.toFixed(1)}% prior month`
                    : undefined}
                />
                <MetricCard
                  label="Revenue/Cover"
                  value={`₹${metrics.revenue_per_cover.toFixed(0)}`}
                  icon={Users}
                  data-testid="metric-revenue-per-cover"
                  subtext={priorMetrics && priorMetrics.revenue_per_cover
                    ? `vs ₹${priorMetrics.revenue_per_cover.toFixed(0)} prior month`
                    : undefined}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-border rounded-md p-6">
                  <h3 className="text-lg font-semibold text-primary mb-4">Revenue Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={metrics.trend_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12, fill: '#5C6B61' }}
                        stroke="#E5E3DB"
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: '#5C6B61' }}
                        stroke="#E5E3DB"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: '#FFFFFF', 
                          border: '1px solid #E5E3DB',
                          borderRadius: '6px'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#1E3F33" 
                        strokeWidth={2}
                        dot={{ fill: '#1E3F33', r: 3 }}
                        name="Revenue"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-border rounded-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-primary">AI Insights</h3>
                    <Sparkles className="h-5 w-5 text-accent" />
                  </div>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {insights.length > 0 ? (
                      insights.map((insight, idx) => (
                        <InsightCard key={idx} insight={insight} />
                      ))
                    ) : (
                      <p className="text-sm text-text-muted">No insights available. Upload more data for AI analysis.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-border rounded-md p-6">
                <h3 className="text-lg font-semibold text-primary mb-4">Cost Breakdown</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={Object.entries(metrics.category_breakdown).map(([name, value]) => ({ name, value }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12, fill: '#5C6B61' }}
                      stroke="#E5E3DB"
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: '#5C6B61' }}
                      stroke="#E5E3DB"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: '#FFFFFF', 
                        border: '1px solid #E5E3DB',
                        borderRadius: '6px'
                      }}
                    />
                    <Bar dataKey="value" fill="#1E3F33" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <SmartUploadZone clientId={selectedClient.id} API={API} onUploadComplete={fetchDashboardData} />
          <PendingImports clientId={selectedClient.id} API={API} module="revenue" onChange={fetchDashboardData} />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ReportsPanel = () => {
  const { selectedClient, API } = useClient();
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const generateReport = async () => {
    if (!selectedClient) return;

    setGenerating(true);
    try {
      let response;
      
      if (reportType === 'weekly') {
        response = await axios.post(
          `${API}/reports/weekly`,
          {
            client_id: selectedClient.id,
            start_date: startDate,
            end_date: endDate
          },
          { responseType: 'blob' }
        );
      } else {
        const date = new Date(startDate);
        response = await axios.post(
          `${API}/reports/monthly`,
          {
            client_id: selectedClient.id,
            year: date.getFullYear(),
            month: date.getMonth() + 1
          },
          { responseType: 'blob' }
        );
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${selectedClient.id}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-md p-8 max-w-2xl mx-auto" data-testid="reports-panel">
      <h3 className="text-2xl font-bold text-primary mb-6">Generate Reports</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Report Type
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full border border-border rounded-md px-4 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            data-testid="report-type-select"
          >
            <option value="weekly">Weekly Brief</option>
            <option value="monthly">Monthly Detailed</option>
          </select>
        </div>

        {reportType === 'weekly' ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-border rounded-md px-4 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="report-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-border rounded-md px-4 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="report-end-date"
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Month
            </label>
            <input
              type="month"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-border rounded-md px-4 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="report-month"
            />
          </div>
        )}

        <button
          onClick={generateReport}
          disabled={generating || !startDate || (reportType === 'weekly' && !endDate)}
          className="w-full bg-primary text-white hover:bg-primary-hover rounded-md px-6 py-3 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="generate-report-button"
        >
          {generating ? 'Generating PDF...' : 'Generate PDF Report'}
        </button>
      </div>
    </div>
  );
};